import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import Cookies from 'js-cookie'

import { getActiveService } from 'components/common/GoogleAnalytics/utils'
import { changePathEvent, initializeGoogleAnalytics, setCustomEvent, setUserEvent } from 'redux/googleAnalytics/slice'
import { useAppSelector } from 'redux/hooks'
import { themeConfig } from 'themes'
import { isProductionEnvironment } from 'utils/common'

const ANALYTICS_TOGGLE_ENABLED = isProductionEnvironment ? themeConfig.ANALYTICS_TOGGLE_ENABLED : true
const GTAG_ID = themeConfig.ANALYTICS_GTAG_ID

const GoogleAnalytics = () => {
    const location = useLocation()
    const dispatch = useDispatch()
    const { isUserSet, gtagEnabled } = useAppSelector(state => state.googleAnalytics)
    const { currentUser } = useAppSelector(state => state.user)
    const { dashboard } = useAppSelector(state => state.dashboard)
    const { currencies } = useAppSelector(state => state.currencies)
    const { allAssets } = useAppSelector(state => state.allAssets)

    /**
     * Handles loading Google Tag Manager script if enabled and cookies are accepted
     */
    const renderCookiesAgreement = () => {
        const cookiesAccepted = JSON.parse(
            localStorage.getItem('CookiePolicyAgreed') || '{ "CookiePolicyAgreed": false }'
        ).CookiePolicyAgreed

        if (cookiesAccepted) {
            dispatch(initializeGoogleAnalytics())
        }
    }

    /**
     * Check if cookies are accepted and render Google Tag Manager script
     */
    useEffect(() => {
        if (GTAG_ID && ANALYTICS_TOGGLE_ENABLED) {
            window.addEventListener('cookiePolicyAgreed', renderCookiesAgreement)
            renderCookiesAgreement()
        }

        return () => {
            window.removeEventListener('cookiePolicyAgreed', renderCookiesAgreement)
        }
    }, [])

    /**
     * Send event to GA when user changes page
     */
    useEffect(() => {
        if (!gtagEnabled) {
            return
        }

        dispatch(
            changePathEvent({
                pathname: location.pathname,
            })
        )
    }, [location.pathname, gtagEnabled])

    /**
     * Send event to GA when user changes
     */
    useEffect(() => {
        if (!currentUser || isUserSet || !gtagEnabled) {
            return
        }
        Cookies.set('user_id', currentUser.identityId)
        // todo: need to check user status and KYC status change and send event to GA

        dispatch(
            setUserEvent({
                identityId: currentUser.identityId,
                status: currentUser.status,
                kycStatus: currentUser.kycStatus,
                userRegistrationIntent: currentUser.userRegistrationIntent,
            })
        )
    }, [currentUser, gtagEnabled])

    /**
     * Send event to GA when user changes active services
     */
    useEffect(() => {
        if (!gtagEnabled || !dashboard || !currentUser || !currencies || !allAssets) {
            return
        }

        const currentDashboard = dashboard[0]

        dispatch(
            setCustomEvent({
                eventAction: 'activeServices',
                eventCategory: 'config',
                value: null,
                kwargs: getActiveService(currentDashboard, currencies, allAssets),
            })
        )
    }, [dashboard, gtagEnabled, currentUser, currencies, allAssets])

    return <></>
}

export default GoogleAnalytics
